import { useEffect, useState } from 'react';
import { useAdmin } from '../../components/AdminLayout';
import { api, type User } from '../../lib/api';
import s from './AdminUsers.module.css';

// ---------------------------------------------------------------------------
// Extended user type for admin view
// ---------------------------------------------------------------------------

interface AdminUser {
  id: string;
  email: string;
  tenants: { id: string; name: string; role: string }[];
  isSuperAdmin: boolean;
  isActive: boolean;
  lastLogin: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminUsers() {
  const { secret } = useAdmin();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  // Confirmation dialog
  const [confirmAction, setConfirmAction] = useState<{
    userId: string;
    type: 'superadmin';
    currentValue: boolean;
  } | null>(null);

  // Fetch users
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.admin
      .users(secret)
      .then(({ users: data }) => {
        if (!cancelled) {
          const rows: AdminUser[] = (data as any[]).map((u) => ({
            id: u.id,
            email: u.email,
            tenants: u.tenants ?? [],
            isSuperAdmin: (u as any).isSuperAdmin ?? (u as any).is_super_admin ?? false,
            isActive: (u as any).isActive ?? (u as any).is_active ?? true,
            lastLogin: (u as any).lastLogin ?? (u as any).last_login ?? null,
          }));
          setUsers(rows);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [secret]);

  // Toggle active
  async function toggleActive(user: AdminUser) {
    setBusyIds((s) => new Set(s).add(user.id));
    try {
      await api.admin.updateUser(secret, user.id, { isActive: !user.isActive } as any);
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isActive: !u.isActive } : u)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    }
  }

  // Toggle super admin (requires confirmation)
  function requestToggleSuperAdmin(user: AdminUser) {
    setConfirmAction({
      userId: user.id,
      type: 'superadmin',
      currentValue: user.isSuperAdmin,
    });
  }

  async function confirmToggleSuperAdmin() {
    if (!confirmAction) return;
    const { userId, currentValue } = confirmAction;
    setConfirmAction(null);

    setBusyIds((s) => new Set(s).add(userId));
    try {
      await api.admin.updateUser(secret, userId, {
        isSuperAdmin: !currentValue,
      } as any);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, isSuperAdmin: !currentValue } : u,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  }

  function formatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  if (loading) {
    return <div className={s.loading}>Loading users&hellip;</div>;
  }

  return (
    <div className={s.page}>
      <h1 className={s.heading}>Users</h1>

      {error && <div className={s.error}>{error}</div>}

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Tenants</th>
              <th>Super Admin</th>
              <th>Active</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{u.email}</td>
                <td>
                  <span className={s.tenantList}>
                    {u.tenants.length > 0
                      ? u.tenants.map((t) => t.name).join(', ')
                      : '\u2014'}
                  </span>
                </td>
                <td>
                  <span className={u.isSuperAdmin ? s.badgeSuperAdmin : s.badgeUser}>
                    {u.isSuperAdmin ? 'Super Admin' : 'User'}
                  </span>
                </td>
                <td>
                  <span
                    className={u.isActive ? s.dotActive : s.dotInactive}
                    title={u.isActive ? 'Active' : 'Inactive'}
                  />
                </td>
                <td>{formatDate(u.lastLogin)}</td>
                <td>
                  <div className={s.actionsCell}>
                    <button
                      type="button"
                      className={s.actionBtn}
                      disabled={busyIds.has(u.id)}
                      onClick={() => toggleActive(u)}
                    >
                      {u.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className={u.isSuperAdmin ? s.actionBtnDanger : s.actionBtn}
                      disabled={busyIds.has(u.id)}
                      onClick={() => requestToggleSuperAdmin(u)}
                    >
                      {u.isSuperAdmin ? 'Revoke Admin' : 'Grant Admin'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation dialog */}
      {confirmAction && (
        <div className={s.confirmOverlay} onClick={() => setConfirmAction(null)}>
          <div className={s.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <h3 className={s.confirmTitle}>
              {confirmAction.currentValue ? 'Revoke Super Admin' : 'Grant Super Admin'}
            </h3>
            <p className={s.confirmText}>
              {confirmAction.currentValue
                ? 'This will remove super admin privileges from this user. They will no longer have access to the admin panel.'
                : 'This will grant super admin privileges to this user. They will have full access to all admin panel features.'}
            </p>
            <div className={s.confirmActions}>
              <button
                type="button"
                className={s.btnGhost}
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={s.btnDanger}
                onClick={confirmToggleSuperAdmin}
              >
                {confirmAction.currentValue ? 'Revoke' : 'Grant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
